import { Injectable } from '@angular/core';
import { SocketMiddlewareService } from './socket-middleware.service';
import { DeviceManagmentEvents } from 'icreate/common/io-event/websocket-message';
import { Subject, takeUntil } from 'rxjs';
import { Device, Model } from 'icreate/common/models/device';

@Injectable({
    providedIn: 'root',
})
export class DeviceManagmentSocketService {
    constructor(private socketService: SocketMiddlewareService) {}

    destroySubscriptions$: Subject<void> = new Subject();
    updateList$: Subject<{
        list: Device[];
        socketData;
        eventType: DeviceManagmentEvents;
        openPage?: boolean;
    }> = new Subject<{
        list: Device[];
        socketData: Device | Model;
        eventType: DeviceManagmentEvents;
    }>();
    deviceManagemntList: Device[];

    startSubscriptions() {
        this.destroySubscriptions();
        this.destroySubscriptions$ = new Subject();
        this.subscribeToEvent(this.deviceCreated$(), DeviceManagmentEvents.DEVICE_CREATED);
        this.subscribeToEvent(this.deviceUpdated$(), DeviceManagmentEvents.DEVICE_UPDATED);
        this.subscribeToEvent(this.deviceDeleted$(), DeviceManagmentEvents.DEVICE_DELETED);
        this.subscribeToEvent(this.modelCreated$(), DeviceManagmentEvents.MODEL_CREATED);
        this.subscribeToEvent(this.modelUpdated$(), DeviceManagmentEvents.MODEL_UPDATED);
        this.subscribeToEvent(this.modelDeleted$(), DeviceManagmentEvents.MODEL_DELETED);
    }

    private subscribeToEvent(event$, eventType) {
        event$.pipe(takeUntil(this.destroySubscriptions$)).subscribe(socketData => {
            let openPage = false;
            if (eventType == DeviceManagmentEvents.DEVICE_CREATED) {
                const { device, senderSocketId } = socketData as {
                    device: Device;
                    senderSocketId: string;
                };
                const currentSocketId = this.socketService.getSocket()?.id;
                socketData.name = device.name;
                openPage = senderSocketId == currentSocketId;
                this.deviceManagemntList.push(device);
            } else if (eventType == DeviceManagmentEvents.DEVICE_UPDATED) {
                const updatedDeviceIndex = this.deviceManagemntList.findIndex(device => device.id == socketData.id);
                if (updatedDeviceIndex < 0) return;
                this.deviceManagemntList.splice(updatedDeviceIndex, 1, socketData);
            } else if (eventType == DeviceManagmentEvents.DEVICE_DELETED) {
                const deletedDeviceIndex = this.deviceManagemntList.findIndex(device => device.id == socketData.id);
                if (deletedDeviceIndex < 0) return;
                this.deviceManagemntList.splice(deletedDeviceIndex, 1);
            } else if (eventType == DeviceManagmentEvents.MODEL_CREATED) {
                const newModel = socketData as Model;
                const deviceIndex = this.deviceManagemntList.findIndex(device => device.id == newModel.equipmentId);
                if (!this.deviceManagemntList[deviceIndex].models) this.deviceManagemntList[deviceIndex].models = [];

                this.deviceManagemntList[deviceIndex].models.push(newModel);
            } else if (eventType == DeviceManagmentEvents.MODEL_UPDATED) {
                const updatedModel = socketData as Model;
                const deviceIndex = this.deviceManagemntList.findIndex(device => device.id == updatedModel.equipmentId);
                if (deviceIndex < 0) return;
                if (!this.deviceManagemntList[deviceIndex].models) return;

                const modelIndex = this.deviceManagemntList[deviceIndex].models.findIndex(
                    model => model.id == updatedModel.id
                );
                if (modelIndex < 0) return;

                this.deviceManagemntList[deviceIndex].models.splice(modelIndex, 1, updatedModel);
            } else if (eventType == DeviceManagmentEvents.MODEL_DELETED) {
                const deletedModel = socketData as Model;
                const deviceId = deletedModel.equipmentId;
                const deviceIndex = this.deviceManagemntList.findIndex(device => device.id == deviceId);
                if (deviceIndex < 0) return;
                if (!this.deviceManagemntList[deviceIndex].models) return;

                const modelIndex = this.deviceManagemntList[deviceIndex].models.findIndex(
                    model => model.id == deletedModel.id
                );
                if (modelIndex < 0) return;
                this.deviceManagemntList[deviceIndex].models.splice(modelIndex, 1);
            }
            this.updateList$.next({
                list: this.deviceManagemntList,
                socketData,
                eventType,
                openPage,
            });
        });
    }

    destroySubscriptions() {
        if (this.destroySubscriptions$) {
            this.destroySubscriptions$.next();
            this.destroySubscriptions$.complete();
        }
        this.destroySubscriptions$ = null;
    }

    updateListSubject() {
        return this.updateList$;
    }

    initializeList(list) {
        this.deviceManagemntList = list;
    }

    private deviceCreated$() {
        return this.socketService.receiveMessageObserver(DeviceManagmentEvents.DEVICE_CREATED);
    }

    private deviceUpdated$() {
        return this.socketService.receiveMessageObserver(DeviceManagmentEvents.DEVICE_UPDATED);
    }

    private deviceDeleted$() {
        return this.socketService.receiveMessageObserver(DeviceManagmentEvents.DEVICE_DELETED);
    }

    private modelCreated$() {
        return this.socketService.receiveMessageObserver(DeviceManagmentEvents.MODEL_CREATED);
    }

    private modelUpdated$() {
        return this.socketService.receiveMessageObserver(DeviceManagmentEvents.MODEL_UPDATED);
    }

    private modelDeleted$() {
        return this.socketService.receiveMessageObserver(DeviceManagmentEvents.MODEL_DELETED);
    }

    equipmentUpdated$() {
        return this.socketService.receiveMessageObserver(DeviceManagmentEvents.EQUIPMENT_UPDATED);
    }
}
